import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import useApiCallHooks from '../../../../hooks/useApiCallHooks';
import AdminAppBody from '../../../../components/admin/AdminAppBody';
import PageHeading from '../../../../components/admin/common/PageHeading';

const QuizAdd = () => {
  const navigate = useNavigate();
  const [callResponse, , , callAPI] = useApiCallHooks();

  const [formData, setFormData] = useState({
    name: '',
    level: '',
    useExcel: false,
    image: '',
    age: '',
    tag: '',
    rating: ''
  });

  const [questions, setQuestions] = useState([
    { question: '', option1: '', option2: '', option3: '', option4: '', correct_option: '' }
  ]);

  const [errors, setErrors] = useState({});
  const [excelFile, setExcelFile] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'checkbox') {
      setFormData({ ...formData, [name]: checked });
    } else if (type === 'file') {
      if (name === 'image') {
        setFormData({ ...formData, [name]: files[0] });
      }
      if (name === 'excel') {
        setExcelFile(files[0]);
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleQuestionChange = (index, field, value) => {
    const updated = [...questions];
    updated[index][field] = value;
    setQuestions(updated);
  };

  const addQuestion = () => {
    setQuestions([...questions, { question: '', option1: '', option2: '', option3: '', option4: '', correct_option: '' }]);
  };

  const deleteQuestion = (index) => {
    const updated = [...questions];
    updated.splice(index, 1);
    setQuestions(updated);
  };

  const handleExcelFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setExcelFile(file);

    const reader = new FileReader();
    reader.onload = (evt) => {
      const data = new Uint8Array(evt.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      const parsedQuestions = jsonData.map((row) => ({
        question: row.question || '',
        option1: row.option1 || '',
        option2: row.option2 || '',
        option3: row.option3 || '',
        option4: row.option4 || '',
        correct_option: row.correct_option || ''
      }));

      setQuestions(parsedQuestions.length ? parsedQuestions : [{
        question: '', option1: '', option2: '', option3: '', option4: '', correct_option: ''
      }]);
    };

    reader.readAsArrayBuffer(file);
  };

  const downloadTemplate = () => {
    const worksheetData = [{ question: '', option1: 1, option2: 2, option3: 3, option4: 4, correct_option: '' }];
    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');
    XLSX.writeFile(workbook, 'quiz_template.xlsx');
  };

  const validate = () => {
    const errs = {};
    if (!formData.name) errs.name = 'Quiz name is required';
    if (!formData.level) errs.level = 'Level is required';
    if (!formData.age) errs.age = 'Age is required';
    if (!formData.tag) errs.tag = 'Tag is required';
    if (!formData.rating) errs.rating = 'Rating is required';
    if (!formData.useExcel && !formData.image) errs.image = 'Quiz image is required';
    if (formData.useExcel && !excelFile) errs.excel = 'Excel file is required';

    if (!formData.useExcel) {
      questions.forEach((q, i) => {
        if (!q.question) errs[`question_${i}`] = 'Question is required';
        for (let j = 1; j <= 4; j++) {
          if (!q[`option${j}`]) errs[`option${j}_${i}`] = `Option ${j} is required`;
        }
        if (!q.correct_option) errs[`correct_${i}`] = 'Correct option is required';
      });
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const data = new FormData();
    Object.entries(formData).forEach(([key, val]) => {
      data.append(key, val);
    });

    if (!formData.useExcel) {
      questions.forEach((q, i) => {
        data.append(`questions[${i}][question]`, q.question);
        data.append(`questions[${i}][option1]`, q.option1);
        data.append(`questions[${i}][option2]`, q.option2);
        data.append(`questions[${i}][option3]`, q.option3);
        data.append(`questions[${i}][option4]`, q.option4);
        data.append(`questions[${i}][correct_option]`, q.correct_option);
      });
    }

    callAPI('post', 'admin/quizzes', data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  };

  if (callResponse?.success) {
    toast.success('Quiz added successfully!');
    setTimeout(() => navigate('/quiz/list'), 1500);
  }

  return (
    <AdminAppBody
      content={
        <div className="container">
          <PageHeading title="Add Quiz" subTitle="Fill out the details of Quiz" />
          <div className="container card p-4 shadow-sm mt-3">
            <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
              <div className="row mb-3">
                <div className="col-md-6">
                  <label>Quiz Name</label>
                  <input name="name" type="text" className="form-control" value={formData.name} onChange={handleChange} />
                  {errors.name && <span className="text-danger">{errors.name}</span>}
                </div>
                <div className="col-md-6">
                  <label>Quiz Image</label>
                  <input type="file" name="image" className="form-control" onChange={handleChange} />
                  {errors.image && <span className="text-danger">{errors.image}</span>}
                </div>
              </div>

              <div className="row mb-3">
                {['age', 'tag', 'rating'].map((field) => (
                  <div className="col-md-4" key={field}>
                    <label>{field.charAt(0).toUpperCase() + field.slice(1)}</label>
                    <input type="text" name={field} className="form-control" value={formData[field]} onChange={handleChange} />
                    {errors[field] && <span className="text-danger">{errors[field]}</span>}
                  </div>
                ))}
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label>Level</label>
                  <select name="level" className="form-control" value={formData.level} onChange={handleChange}>
                    <option value="">Select Level</option>
                    <option value="basic">Basic</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advance">Advance</option>
                  </select>
                  {errors.level && <span className="text-danger">{errors.level}</span>}
                </div>
                <div className="col-md-6">
                  <label>Use Excel File</label>
                  <div className="form-check mt-2">
                    <input type="checkbox" name="useExcel" className="form-check-input" checked={formData.useExcel} onChange={handleChange} />
                    <label className="form-check-label">Yes</label>
                  </div>
                </div>
              </div>

              {formData.useExcel ? (
                <div className="mb-3">
                  <label>Upload Excel File</label>
                  <input type="file" name="excel" className="form-control" onChange={handleExcelFile} accept=".xlsx, .xls" />
                  {errors.excel && <span className="text-danger">{errors.excel}</span>}
                  <button type="button" className="btn btn-secondary mt-2" onClick={downloadTemplate}>
                    Download Excel Template
                  </button>
                </div>
              ) : (
                <div>
                  {questions.map((q, idx) => (
                    <div key={idx} className="border p-3 mb-3">
                      <h6>Question {idx + 1}</h6>
                      <input type="text" className="form-control mb-1" placeholder="Enter question"
                        value={q.question} onChange={(e) => handleQuestionChange(idx, 'question', e.target.value)} />
                      {errors[`question_${idx}`] && <span className="text-danger">{errors[`question_${idx}`]}</span>}

                      {[1, 2, 3, 4].map((num) => (
                        <div key={num}>
                          <input type="text" className="form-control mb-1" placeholder={`Option ${num}`}
                            value={q[`option${num}`]} onChange={(e) => handleQuestionChange(idx, `option${num}`, e.target.value)} />
                          {errors[`option${num}_${idx}`] && <span className="text-danger">{errors[`option${num}_${idx}`]}</span>}
                        </div>
                      ))}

                      <input type="text" className="form-control mb-1" placeholder="Correct answer"
                        value={q.correct_option} onChange={(e) => handleQuestionChange(idx, 'correct_option', e.target.value)} />
                      {errors[`correct_${idx}`] && <span className="text-danger">{errors[`correct_${idx}`]}</span>}

                      {questions.length > 1 && (
                        <button type="button" className="btn btn-danger mt-2" onClick={() => deleteQuestion(idx)}>
                          Delete
                        </button>
                      )}
                    </div>
                  ))}
                  <button type="button" className="btn btn-primary mb-3" onClick={addQuestion}>
                    Add Question
                  </button>
                </div>
              )}

              <button type="submit" className="btn btn-success">Submit Quiz</button>
            </form>
          </div>
        </div>
      }
    />
  );
};

export default QuizAdd;
